package uz.mint.frontend.api

import androidx.compose.runtime.*
import uz.mint.frontend.i18n.Locale

/** What the destination picker knows at any moment. */
sealed interface DestinationState {
    data object Loading : DestinationState
    data class Loaded(val destinations: Destinations) : DestinationState
    data class Failed(val message: String) : DestinationState
}

/**
 * Loads the destination index once per locale.
 *
 * No paging and no debounce, unlike [rememberPlanPage]: this is one response covering the whole
 * catalogue's ~190 countries and ~35 bundles — a few kilobytes — and **searching it happens in the
 * browser**. That inverts the rule the plan grid follows for a reason: filtering plans client-side
 * would mean downloading 2 900 of them, while filtering an already-loaded list of 225 names is
 * instant and costs no request per keystroke.
 *
 * @return the state, plus a retry that re-runs the request.
 */
@Composable
fun rememberDestinations(locale: Locale): Pair<DestinationState, () -> Unit> {
    var attempt by remember { mutableStateOf(0) }
    var state by remember { mutableStateOf<DestinationState>(DestinationState.Loading) }

    LaunchedEffect(locale, attempt) {
        state = DestinationState.Loading
        state = try {
            DestinationState.Loaded(DestinationApi.fetchDestinations(locale))
        } catch (e: Throwable) {
            // Same caveat as the plan grid: the static export snapshots from a headless browser
            // that may not reach the backend, so this state can be baked into the exported HTML.
            // Kobweb re-runs the composition on load and the real result replaces it.
            DestinationState.Failed(e.message ?: "destinations unavailable")
        }
    }

    return state to { attempt++ }
}
